import React from "react";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

const LINK_CLS = "text-orange font-semibold underline underline-offset-2 hover:text-orange-dark transition-colors";
const LINK_RE = /(\[[^\]]+\]\([^)]+\))|(https?:\/\/[^\s]+)|((?<![\w])\/[a-zA-Z][a-zA-Z0-9\-/]*)/g;

const Anchor = ({ href, label, k }) => {
  const isInternal = href.startsWith("/");
  if (isInternal) return <Link key={k} to={href} className={LINK_CLS} data-testid="rich-inline-link">{label}</Link>;
  return <a key={k} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLS} data-testid="rich-inline-link">{label}</a>;
};

// Turns plain text into React nodes with clickable links (markdown links, bare URLs, internal paths).
export const renderLinks = (text, keyBase = "") => {
  const parts = [];
  let last = 0, m, i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [full, md, url, path] = m;
    if (md) {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(md);
      parts.push(<Anchor key={`${keyBase}a${i++}`} href={mm[2]} label={mm[1]} k={`${keyBase}a${i}`} />);
    } else if (url) {
      const trail = (url.match(/[.,;:!?)]+$/) || [""])[0];
      const clean = trail ? url.slice(0, -trail.length) : url;
      parts.push(<Anchor key={`${keyBase}u${i++}`} href={clean} label={clean} k={`${keyBase}u${i}`} />);
      if (trail) parts.push(trail);
    } else if (path) {
      parts.push(<Anchor key={`${keyBase}p${i++}`} href={path} label={path} k={`${keyBase}p${i}`} />);
    }
    last = m.index + full.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// Inline formatting: **bold** on top of link parsing.
export const renderInline = (text) => {
  const out = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...renderLinks(text.slice(last, m.index), `n${i}`));
    out.push(<strong key={`b${i++}`} className="font-bold text-navy">{renderLinks(m[1], `b${i}`)}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...renderLinks(text.slice(last), `n${i}`));
  return out;
};

const renderBlock = (line, key) => {
  const h3 = /^###\s+(.*)$/.exec(line);
  if (h3) return <h3 key={key} data-testid="rich-h3" className="font-display font-bold text-xl md:text-2xl text-navy mt-8 mb-3">{renderInline(h3[1])}</h3>;
  const h2 = /^##?\s+(.*)$/.exec(line);
  if (h2) return <h2 key={key} data-testid="rich-h2" className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-10 mb-4">{renderInline(h2[1])}</h2>;
  return <p key={key} className="text-slate-600 leading-relaxed text-lg mb-6">{renderInline(line)}</p>;
};

const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+\.\s+(.*)$/;
const QUOTE_RE = /^\s*>\s+(.*)$/;

// Renders an array of content lines, grouping bullet/numbered lists and > pull quotes.
export const renderContent = (lines) => {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const isOrdered = ORDERED_RE.test(lines[i]);
    const isBullet = BULLET_RE.test(lines[i]);
    const isQuote = QUOTE_RE.test(lines[i]);
    if (isQuote) {
      const parts = [];
      const start = i;
      while (i < lines.length && QUOTE_RE.test(lines[i])) { parts.push(QUOTE_RE.exec(lines[i])[1]); i++; }
      blocks.push(
        <blockquote key={`q${start}`} data-testid="rich-pullquote" className="my-10 border-l-4 border-orange pl-6 md:pl-8 py-2">
          <p className="font-display font-bold text-2xl md:text-3xl text-navy leading-snug">{renderInline(parts.join(" "))}</p>
        </blockquote>
      );
    } else if (isOrdered || isBullet) {
      const re = isOrdered ? ORDERED_RE : BULLET_RE;
      const items = [];
      const start = i;
      while (i < lines.length && re.test(lines[i])) { items.push(re.exec(lines[i])[1]); i++; }
      const Tag = isOrdered ? "ol" : "ul";
      blocks.push(
        <Tag key={`list${start}`} data-testid={isOrdered ? "rich-ol" : "rich-ul"} className={`${isOrdered ? "list-decimal" : "list-disc"} pl-6 mb-6 space-y-2 text-slate-600 text-lg marker:text-orange marker:font-bold`}>
          {items.map((it, j) => <li key={j} className="leading-relaxed pl-1">{renderInline(it)}</li>)}
        </Tag>
      );
    } else {
      blocks.push(renderBlock(lines[i], `blk${i}`));
      i++;
    }
  }
  return blocks;
};

// Convenience: render a multiline string body.
export const RichText = ({ text, className = "" }) => {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;
  return <div className={className}>{renderContent(lines)}</div>;
};

// Detects whether a stored value is WYSIWYG HTML (vs the legacy shorthand).
export const isHtml = (str) => /<(p|h[1-6]|ul|ol|li|strong|em|b|i|a|blockquote|br)[\s>/]/i.test(str || "");

const escapeHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const inlineToHtml = (text) => {
  let t = escapeHtml(text);
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, label, href) => `<a href="${href}">${label}</a>`);
  t = t.replace(/(^|[\s(])((https?:\/\/[^\s)]+))/g, (m, pre, url) => `${pre}<a href="${url}">${url}</a>`);
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return t;
};

// Converts the legacy shorthand into HTML so old content can be edited in the WYSIWYG.
export const shorthandToHtml = (text) => {
  const lines = (text || "").split("\n").map((l) => l.trimEnd());
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    if (/^###\s+/.test(line)) { out.push(`<h3>${inlineToHtml(line.replace(/^###\s+/, ""))}</h3>`); i++; continue; }
    if (/^##?\s+/.test(line)) { out.push(`<h2>${inlineToHtml(line.replace(/^##?\s+/, ""))}</h2>`); i++; continue; }
    if (/^\s*>\s+/.test(line)) {
      const parts = [];
      while (i < lines.length && /^\s*>\s+/.test(lines[i].trim())) { parts.push(inlineToHtml(lines[i].trim().replace(/^\s*>\s+/, ""))); i++; }
      out.push(`<blockquote><p>${parts.join(" ")}</p></blockquote>`); continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i].trim())) { items.push(`<li>${inlineToHtml(lines[i].trim().replace(/^\s*\d+\.\s+/, ""))}</li>`); i++; }
      out.push(`<ol>${items.join("")}</ol>`); continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i].trim())) { items.push(`<li>${inlineToHtml(lines[i].trim().replace(/^\s*[-*]\s+/, ""))}</li>`); i++; }
      out.push(`<ul>${items.join("")}</ul>`); continue;
    }
    out.push(`<p>${inlineToHtml(line)}</p>`); i++;
  }
  return out.join("");
};

// Renders either legacy shorthand OR WYSIWYG HTML. Internal links keep SPA navigation.
export const RichContent = ({ text, className = "" }) => {
  const navigate = useNavigate();
  if (!text) return null;
  if (!isHtml(text)) return <RichText text={text} className={className} />;
  const clean = DOMPurify.sanitize(text, { ADD_ATTR: ["target", "rel"] });
  const onClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("/")) { e.preventDefault(); navigate(href); }
  };
  return <div className={`rich-html ${className}`} onClick={onClick} dangerouslySetInnerHTML={{ __html: clean }} />;
};
